import { withHandSetDurations } from './stylePace';
import { VideoScene } from './types';

type StoredCrop = NonNullable<VideoScene['imageCrop']>;

const isCrop = (value: unknown): value is StoredCrop => {
  if (!value || typeof value !== 'object') return false;
  const crop = value as Record<string, unknown>;
  return (
    typeof crop.unit === 'string' &&
    ['x', 'y', 'width', 'height'].every(
      (key) => typeof crop[key] === 'number' && Number.isFinite(crop[key])
    )
  );
};

/**
 * SYM-6272. The scenes with the crops the draft already holds, matched by
 * position like the by-hand flags (withHandSetDurations): the scenes come from
 * the preview composition, the crops from the draft's own slides, and they
 * list the same slides in the same order.
 *
 * Why this exists. Since SYM-6272 every update names the crop of every slide,
 * `null` when the wizard holds none, because a crop left out of an update is
 * kept by the server and ended up on a swapped picture. That makes what the
 * wizard HOLDS the truth. The scenes used to start without any crop, whatever
 * the draft held, so opening a draft that has a cropped slide and changing
 * one word elsewhere would have told the server "no crop" and wiped it (gate
 * review, 19 Sep 2026). A wizard that speaks for every crop has to know them.
 */
export function withStoredCrops(
  scenes: VideoScene[],
  // Deliberately loose: the draft's slides are typed by what the preview needs
  // (PreviewSlide), which does not describe the `image` node the server stores.
  slides: ReadonlyArray<unknown> | null | undefined
): VideoScene[] {
  if (!Array.isArray(slides)) return scenes;

  return scenes.map((scene, i) => {
    const slide = slides[i] as { image?: { crop?: unknown } | null } | null;
    const stored = slide?.image?.crop;
    return isCrop(stored) && !scene.imageCrop
      ? { ...scene, imageCrop: stored }
      : scene;
  });
}

const TRANSITIONS: ReadonlyArray<VideoScene['transition']> = [
  'cut',
  'fade',
  'dissolve',
  'slide',
];

/** The wizard's word for a transition the draft stores (slide-left -> slide). */
const wizardTransition = (stored: unknown): VideoScene['transition'] | null => {
  if (typeof stored !== 'string') return null;
  const word = stored.startsWith('slide') ? 'slide' : stored;
  return (TRANSITIONS as ReadonlyArray<string>).includes(word)
    ? (word as VideoScene['transition'])
    : null;
};

/**
 * SYM-6185. The scenes with the supporting lines, and the transitions set by
 * hand, that the draft already holds, matched by position like the crops.
 *
 * The preview composition the scenes are parsed from carries neither: every
 * scene starts with an empty supporting line and "fade". For a fresh proposal
 * that was the truth. A REOPENED video has both, and the wizard sends every
 * slide's supporting line and transition on each update, so starting without
 * them would wipe them the first time one word is changed elsewhere (gate
 * review, 19 Sep 2026). Only a transition marked by hand is loaded: any other
 * is the look's business, not the slide's.
 */
export function withStoredChoices(
  scenes: VideoScene[],
  slides: ReadonlyArray<unknown> | null | undefined
): VideoScene[] {
  if (!Array.isArray(slides)) return scenes;

  return scenes.map((scene, i) => {
    const slide = slides[i] as {
      subtext?: unknown;
      transition?: unknown;
      transition_set_by_hand?: unknown;
      duration?: unknown;
      duration_set_by_hand?: unknown;
    } | null;
    if (!slide) return scene;

    const subtext =
      typeof slide.subtext === 'string' &&
      slide.subtext !== '' &&
      !scene.subtext
        ? slide.subtext
        : null;
    const transition =
      slide.transition_set_by_hand === true
        ? wizardTransition(slide.transition)
        : null;
    // A length set by hand is the customer's number. The composition's is the
    // one the timeline worked out from it, which may have been held back to
    // fit a changeover; sending that back would quietly replace the choice.
    const duration =
      slide.duration_set_by_hand === true &&
      typeof slide.duration === 'number' &&
      Number.isFinite(slide.duration) &&
      slide.duration > 0 &&
      slide.duration !== scene.duration
        ? slide.duration
        : null;
    if (subtext === null && transition === null && duration === null)
      return scene;

    return {
      ...scene,
      ...(subtext !== null ? { subtext } : {}),
      ...(transition !== null ? { transition } : {}),
      ...(transition !== null && slide.transition !== transition
        ? { transitionAsStored: slide.transition as string }
        : {}),
      ...(duration !== null ? { duration } : {}),
    };
  });
}

const wholeNumber = (value: unknown, least: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= least;

/**
 * SYM-6185. The scenes with the PICTURE each slide of the draft points at,
 * matched by position.
 *
 * A scene's `index` is not its place in the video: it is which of the
 * campaign's pictures it shows, and it is what every update sends as
 * `image.index` (dragging slides around keeps it, so the picture travels with
 * its slide). Parsed from the preview composition, the scenes are simply
 * numbered 0, 1, 2 in the order they play. For a fresh proposal those are the
 * same thing. A REOPENED video whose slides were reordered, or whose picture
 * was swapped for one from the library (`resource_id`), is shown correctly by
 * the composition but would then be sent back as pictures 0, 1, 2: other
 * pictures under the same texts and crops (gate review, 19 Sep 2026).
 */
export function withStoredPictures(
  scenes: VideoScene[],
  slides: ReadonlyArray<unknown> | null | undefined
): VideoScene[] {
  if (!Array.isArray(slides)) return scenes;

  return scenes.map((scene, i) => {
    const image = (slides[i] as { image?: unknown } | null)?.image as
      | { index?: unknown; resource_id?: unknown }
      | null
      | undefined;
    if (!image || typeof image !== 'object') return scene;

    const index = wholeNumber(image.index, 0) ? image.index : scene.index;
    const resourceId =
      wholeNumber(image.resource_id, 1) && typeof scene.resourceId !== 'number'
        ? image.resource_id
        : scene.resourceId;
    if (index === scene.index && resourceId === scene.resourceId) return scene;

    return {
      ...scene,
      index,
      ...(typeof resourceId === 'number' ? { resourceId } : {}),
    };
  });
}

type StoredText = Record<string, unknown> & { function: string };

/** The first slide's text as the draft stores it: banner word + job line. */
const storedTextObject = (text: unknown): StoredText | null =>
  text &&
  typeof text === 'object' &&
  !Array.isArray(text) &&
  typeof (text as { function?: unknown }).function === 'string'
    ? (text as StoredText)
    : null;

/**
 * SYM-6185. The scenes with each headline as the DRAFT holds it.
 *
 * The preview composition is made to be looked at. The server lays a headline
 * out for it: breaks it into lines, in the middle of a word if it has to, and
 * past four lines cuts it off with "...". The scenes are parsed from it and
 * every update sends every headline back, so an untouched long headline came
 * back broken, or cut short for good, the first time anything else changed
 * (gate review, 19 Sep 2026). The composition is BUILT from the draft's
 * slides, so the draft's words are the same words before the layout, and they
 * are what the editor should hold.
 *
 * The first slide's text is stored as banner word + job line. The editor shows
 * the job line; the whole stored text is kept next to it (`textAsStored`) so
 * that the banner word goes back with every update instead of being replaced
 * by the default one.
 */
export function withStoredHeadlines(
  scenes: VideoScene[],
  slides: ReadonlyArray<unknown> | null | undefined
): VideoScene[] {
  if (!Array.isArray(slides)) return scenes;

  return scenes.map((scene, i) => {
    const text = (slides[i] as { text?: unknown } | null)?.text;
    const whole = storedTextObject(text);
    const words = whole ? whole.function : typeof text === 'string' ? text : '';
    if (words.trim() === '') return scene;

    return words === scene.headline && !whole
      ? scene
      : {
          ...scene,
          headline: words,
          ...(whole ? { textAsStored: whole } : {}),
        };
  });
}

/**
 * What to send as the text of the slide at `position`.
 *
 * The banner word belongs to the FIRST PLACE in the video, not to the slide
 * that happened to be there when the draft was loaded: the server reads a
 * banner word + job line on the first slide only, and on any later slide it
 * would print the two glued together. So whichever slide plays first goes out
 * inside the stored text, and every other slide as its plain words, wherever
 * the stored text was loaded.
 */
export function textToSend(
  scenes: ReadonlyArray<Pick<VideoScene, 'headline' | 'textAsStored'>>,
  position: number
): string | Record<string, unknown> {
  const headline = scenes[position]?.headline ?? '';
  if (position !== 0) return headline;

  const stored = scenes.find((scene) => scene.textAsStored)?.textAsStored;
  return stored ? { ...stored, function: headline } : headline;
}

/**
 * The scenes without one of them. The stored first-slide text (banner word)
 * belongs to the video, so when the slide that carries it goes, the first
 * slide that stays takes it over.
 */
export function withoutScene(
  scenes: VideoScene[],
  sceneId: string
): VideoScene[] {
  const gone = scenes.find((scene) => scene.id === sceneId);
  const rest = scenes.filter((scene) => scene.id !== sceneId);
  const stored = gone?.textAsStored;
  if (!stored || rest.length === 0) return rest;

  return rest.map((scene, i) =>
    i === 0 ? { ...scene, textAsStored: stored } : scene
  );
}

/**
 * Everything above, in the one order that is tested: the scenes as parsed from
 * the preview composition, completed with what the draft's own slides hold.
 */
export function withWhatTheDraftHolds(
  scenes: VideoScene[],
  slides: ReadonlyArray<unknown> | null | undefined
): VideoScene[] {
  const byHand = withHandSetDurations(
    scenes,
    slides as Parameters<typeof withHandSetDurations>[1]
  );
  return [
    withStoredCrops,
    withStoredChoices,
    withStoredPictures,
    withStoredHeadlines,
  ].reduce((held, load) => load(held, slides), byHand);
}

/**
 * What to send as a slide's transition: the draft's own word while the scene
 * still shows it (slide-up, shown as slide), the wizard's word otherwise.
 */
export function transitionToSend(
  scene: Pick<VideoScene, 'transition' | 'transitionAsStored'>
): string {
  const stored = scene.transitionAsStored;
  return typeof stored === 'string' &&
    wizardTransition(stored) === scene.transition
    ? stored
    : scene.transition;
}
